import React, { useMemo, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner, Toolbar } from '~components';
import Icon, { faInfoCircle } from '~components/icon';

import { useGetDashboardBudgetsQuery } from '~support-at-home/actions/dashboardBudgets';
import { ExpandedTable } from '~support-at-home/models/dashboard';

import { DashboardPieChart } from './DashboardPieChart';
import { DashboardSummary } from './DashboardSummary';
import { DashboardTable } from './DashboardTable';
import DashboardTableModal from './DashboardTableModal';
import { QuarterYearSelector } from './QuarterYearSelector';
import {
  cmClientColumns,
  cmWorkersColumns,
  getCurrentPeriod,
  mapCmClientsToRows,
  mapCmWorkersToRows,
  mapOverUtilisedToRows,
  overUtilisedColumns,
  transformCmOverviewPieData,
  transformCompanyOverviewPieData,
} from './utils';

export const BudgetDashboard: React.FC = () => {
  const [{ quarter: initialQuarter, year: initialYear }] = useState(getCurrentPeriod);
  const [quarter, setQuarter] = useState(initialQuarter);
  const [year, setYear] = useState(initialYear);
  const [expandedTable, setExpandedTable] = useState<ExpandedTable>(null);

  const { data, isLoading, isFetching, isError, error } = useGetDashboardBudgetsQuery({ quarter, year });

  const companyPieData = useMemo(
    () => (data ? transformCompanyOverviewPieData(data.company_budget_overview) : []),
    [data],
  );

  const cmPieData = useMemo(() => (data ? transformCmOverviewPieData(data.case_management_overview) : []), [data]);

  const overUtilisedRows = useMemo(() => (data ? mapOverUtilisedToRows(data.top_over_utilised) : []), [data]);

  const overUtilisedCmRows = useMemo(() => (data ? mapCmClientsToRows(data.over_utilised_cm) : []), [data]);

  const underUtilisedCmRows = useMemo(() => (data ? mapCmClientsToRows(data.under_utilised_cm) : []), [data]);

  const cmWorkersRows = useMemo(() => (data ? mapCmWorkersToRows(data.top_cm_workers) : []), [data]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading" role="status" aria-live="polite">
          <Spinner loading={true} type="beat" size={10} />
          Loading dashboard data...
        </div>
      );
    }

    if (isError) {
      return (
        <ErrorResponse
          message={`Failed to load dashboard data - ${getErrorMessage(error)}`}
          dataCy="budget-dashboard-error"
        />
      );
    }

    if (!data) {
      return (
        <div className="empty" role="status">
          <Icon icon={faInfoCircle} />
          No budget data for the selected period.
        </div>
      );
    }

    return (
      <>
        <DashboardSummary summary={data.summary} />

        <div className="grid">
          <DashboardPieChart
            title="Company Budget Overview"
            description="Booked, Claimed, and Remaining budget across all clients"
            data={companyPieData}
          />

          <DashboardTable
            title="Top 10 Over-Utilised"
            description="Budgets where utilisation exceeds 100%"
            columns={overUtilisedColumns}
            data={overUtilisedRows}
            dataCy="dashboard-top-over-utilised-table"
            onExpand={() => setExpandedTable('over-utilised')}
          />

          <DashboardPieChart
            title="Case Management Overview"
            description="CM budget allocation and usage across all clients"
            data={cmPieData}
          />

          <DashboardTable
            title="Over-Utilised CM"
            description="Clients whose CM usage exceeds allocation"
            columns={cmClientColumns}
            data={overUtilisedCmRows}
            dataCy="dashboard-over-utilised-cm-table"
            onExpand={() => setExpandedTable('over-utilised-cm')}
          />

          <DashboardTable
            title="Under-Utilised CM"
            description="Clients with lowest CM usage relative to allocation"
            columns={cmClientColumns}
            data={underUtilisedCmRows}
            dataCy="dashboard-under-utilised-cm-table"
            onExpand={() => setExpandedTable('under-utilised-cm')}
          />

          <DashboardTable
            title="Top CM Workers"
            description="Workers ranked by total CM cost (booked + claimed)"
            columns={cmWorkersColumns}
            data={cmWorkersRows}
            dataCy="dashboard-top-cm-workers-table"
            onExpand={() => setExpandedTable('cm-workers')}
          />
        </div>

        <DashboardTableModal
          expandedTable={expandedTable}
          quarter={quarter}
          year={year}
          onClose={() => setExpandedTable(null)}
        />
      </>
    );
  };

  return (
    <div
      className={`budget-dashboard${isFetching ? ' fetching' : ''}`}
      data-cy="budget-dashboard"
      aria-busy={isFetching}
    >
      <Toolbar
        id="budget-dashboard"
        leftCluster={
          <div>
            <h5 className="mb-0 fw-bold">Company Budget Dashboard</h5>
            <small className="text-muted">Executive overview of all client budgets and risk management</small>
          </div>
        }
        rightCluster={
          <QuarterYearSelector quarter={quarter} year={year} onQuarterChange={setQuarter} onYearChange={setYear} />
        }
      />
      {renderContent()}
    </div>
  );
};
